import { Button } from "@/components/ui/button";
import { DialogFooter, DialogHeader } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import AutoComplete from "@/components/autocomplete";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addTestKit } from "@/redux/actions/testKitActions";
import { getAllDoctors } from "@/redux/actions/usersActions";
import useDebounce from "@/hooks/use-debounce";
import { RootState } from "@/redux/reducers";
import { UserType } from "@/common/types";
import { useToast } from "@/hooks/use-toast";
import { Checkbox } from "@/components/ui/checkbox";

const AddTestKit = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [formState, setFormState] = useState({
    kit_ids: "",
    doctor: null,
    is_child_test_kit: false,
  });
  const [error, setError] = useState<string>("");

  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const { doctors } = useSelector((state: RootState) => state.users);

  const dispatch: any = useDispatch();
  const { toast } = useToast();

  const handleChange = (key: string, value: string | boolean) => {
    setFormState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const validateInput = () => {
    let errorMessage = "";
    if (!formState.kit_ids || formState.kit_ids === "") {
      errorMessage = "Kit IDs are required";
    } else {
      errorMessage = "";
    }
    setError(errorMessage);
    return errorMessage;
  };

  const handleSubmit = () => {
    const errorMessage = validateInput();
    if (!errorMessage) {
      const payload = {
        kit_ids: formState.kit_ids.split(",").map((item) => item.trim()),
        doctor: formState.doctor || null,
        is_child_test_kit: formState.is_child_test_kit,
      };
      dispatch(
        addTestKit(payload, (message) => {
          setIsOpen(false);
          toast({
            description: message,
          });
        })
      );
    }
  };

  useEffect(() => {
    if (isOpen) dispatch(getAllDoctors(debouncedSearchTerm));
  }, [isOpen, debouncedSearchTerm]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="secondary">Add New</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Add Test Kit</DialogTitle>
          <DialogDescription>
            Add a new test kit to your dashboard.
          </DialogDescription>
          <DialogDescription>
            To add multiple kits enter comma separated kit ids
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="kit_id" className="text-right">
              Kit ID*
            </Label>
            <Input
              id="kit_id"
              name="kit_id"
              className="col-span-3"
              placeholder="Kit ID"
              onChange={(e) => handleChange("kit_ids", e.target.value)}
              required
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="doctor" className="text-right">
              Doctor
            </Label>
            <div className="col-span-3">
              <AutoComplete
                placeHolder="doctor"
                options={doctors as UserType[]}
                labelKey="name"
                onSelect={(option) => handleChange("doctor", option?.id)}
                onChange={(search) => setSearchTerm(search)}
                listCount={5}
              />
            </div>
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <div className="col-span-1 flex justify-end">
              <Checkbox
                id="child-test-kit"
                onCheckedChange={(checked) =>
                  handleChange("is_child_test_kit", checked)
                }
              />
            </div>
            <div className="col-span-3">
              <label htmlFor="child-test-kit" className="text-sm">
                Mark as Child Test Kit
              </label>
            </div>
          </div>
          {error && (
            <div className="grid grid-cols-4 items-center gap-4">
              <div className="col-span-1"></div>
              <div className="col-span-3">
                <p className="text-red-500 text-sm">{error}</p>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button type="submit" onClick={() => handleSubmit()}>
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddTestKit;
